import { z } from 'zod';

import { COMPARISON_BLOCKERS } from '../../libs/account/finding-compare';

// ---------------------------------------------------------------------------
// Delivery evidence (#2957) — what a licensed capture comparison is allowed to
// conclude about owned work, and the provenance that keeps the conclusion
// readable later.
//
// This contract adds NO comparison logic. `compareCaptures` in
// `libs/account/finding-compare.ts` already decides what two captures license;
// everything here is about carrying that decision onto a Krux and recording
// precisely which two captures, which run, which commits and which area
// revision produced it. The reason it is a contract rather than a local type is
// criterion 2 of #2957: the provenance has to survive the comparison that made
// it, so it has to be a persistable shape rather than an in-memory object.
//
// The one invariant worth stating up front: a `delivered` outcome is the only
// one that closes work, and it is reachable from exactly one comparison state
// under exactly one licence. Every other state maps to something that keeps the
// work open.
// ---------------------------------------------------------------------------

/**
 * What one comparison concluded about one piece of owned work.
 *
 * Five values, because collapsing any two of them loses a distinction somebody
 * acts on:
 *
 * - `delivered` — the finding disappeared AND the comparison licensed the
 *   disappearance. The only outcome that closes work.
 * - `regressed` — the finding is present again after a recorded delivery. Rests
 *   on positive evidence (both captures SAW it, or the candidate did), never on
 *   silence.
 * - `retained` — present now, and nobody claimed otherwise. Positive evidence
 *   that the work is still open.
 * - `pending` — the comparison addressed the finding and could not license a
 *   claim: capped, unreadable, a failed tool, config/analyzer drift, or rules
 *   nothing observed. Distinct from `retained` because the work may in fact be
 *   done and this comparison simply cannot say so.
 * - `not-comparable` — the comparison cannot address this finding at all:
 *   different repository, different area scope, the same capture twice, or a
 *   finding neither side reported inside the scope.
 *
 * `pending` and `not-comparable` are both open, and they are kept apart because
 * the fix differs: `pending` is resolved by a cleaner rescan, `not-comparable`
 * by comparing the right two things.
 */
export const KRUX_DELIVERY_OUTCOMES = [
  'delivered',
  'regressed',
  'retained',
  'pending',
  'not-comparable',
] as const;
export const KruxDeliveryOutcomeSchema = z.enum(KRUX_DELIVERY_OUTCOMES);
export type KruxDeliveryOutcome = z.infer<typeof KruxDeliveryOutcomeSchema>;

/**
 * Whether the evidence behind an outcome can still be inspected.
 *
 * Criterion 2 of #2957 requires a reference to stay inspectable after graph
 * retention or to say explicitly what expired. Captures outlive the
 * `repo_scans` rows a run is named by, so the two can rot independently and
 * each is reported on its own terms.
 *
 * `unrecorded` is not a synonym for `expired`: it means nothing ever resolved
 * the reference, which licenses no claim either way and must never be read as a
 * surviving capture.
 */
export const KRUX_EVIDENCE_REACHABILITY = [
  'inspectable',
  'expired',
  'unrecorded',
] as const;
export const KruxEvidenceReachabilitySchema = z.enum(
  KRUX_EVIDENCE_REACHABILITY,
);
export type KruxEvidenceReachability = z.infer<
  typeof KruxEvidenceReachabilitySchema
>;

/** The comparison qualifications, reusing the comparator's own vocabulary. */
export const ComparisonQualificationSchema = z.enum(COMPARISON_BLOCKERS);

/**
 * The provenance of one delivery outcome, in full.
 *
 * Deliberately exact rather than summarised. A stored outcome that names only
 * "a scan" is an assertion; one that names both capture ids, both commits, the
 * run, and the area revision it was scoped by can be re-derived by a reader who
 * doubts it — which is the entire point of #2957, and why none of these are
 * optional.
 */
export const KruxDeliveryEvidenceSchema = z.object({
  /** The earlier capture. Named, never inferred. */
  baselineCaptureId: z.string().min(1),
  /** The later capture. Named, never inferred. */
  candidateCaptureId: z.string().min(1),
  baselineHeadSha: z.string().min(1),
  candidateHeadSha: z.string().min(1),
  /**
   * The scan run that produced the candidate capture.
   *
   * Nullable because a capture can outlive the run row that made it, and a run
   * id invented to fill the field would be worse than its absence.
   */
  runId: z.string().min(1).nullable(),
  /** The area, and the exact revision the comparison was scoped by. */
  areaId: z.string().min(1),
  areaRevision: z.number().int().positive(),
  scopeKey: z.string().min(1),
  /** The finding whose identity the outcome is about. */
  findingId: z.string().min(1),
  /**
   * Every reason the comparison could not license a claim, in full.
   *
   * All of them rather than the first, on the comparator's own reasoning: a
   * reader who resolves one and re-runs must not discover a second that was
   * there the whole time.
   */
  qualifications: z.array(ComparisonQualificationSchema),
  /** Whether the CANDIDATE's silence was evidence — what licenses `delivered`. */
  absenceLicensed: z.boolean(),
  /** Whether the BASELINE's silence was evidence — what licenses a new finding. */
  baselineAbsenceLicensed: z.boolean(),
  /** Whether THIS finding's own tool had its rules established on both sides. */
  rulesObserved: z.boolean(),
  /** The tools whose rules were not established, on either side. */
  unobservedRuleTools: z.array(z.string().min(1)),
  /** How many occurrences each side saw. Multiplicity is part of the answer. */
  baselineOccurrences: z.number().int().nonnegative(),
  candidateOccurrences: z.number().int().nonnegative(),
  /**
   * Whether the two captures could be read back WHEN THIS WAS RECORDED.
   *
   * Durable provenance, deliberately frozen: it says what the comparison could
   * see at the moment it was made, which is part of the record and must not be
   * rewritten later. It is NOT a claim about now — retention moves underneath a
   * stored row — so a reader wanting the current answer reads
   * `KruxEvent.deliveryReachability`, which is resolved on every read.
   */
  captureReachability: KruxEvidenceReachabilitySchema,
  observedAt: z.iso.datetime(),
});
export type KruxDeliveryEvidence = z.infer<typeof KruxDeliveryEvidenceSchema>;

/**
 * What one VERIFICATION RUN concluded, which is wider than what a comparison
 * can conclude.
 *
 * A run has to establish its inputs before any comparison exists, and each way
 * of failing to points somewhere different: a reader told `baseline-expired`
 * should re-accept against a surviving capture, one told `candidate-expired`
 * should name a different candidate, and one told `area-expired` has lost the
 * scope the work was pinned under. Reporting all three as one state would send
 * two of those readers to the wrong place.
 *
 * `not-accepted` is the one that must never be silently upgraded: nothing was
 * accepted, so there is no delivery to verify and a scan must not create one.
 */
export const FINDING_DELIVERY_OUTCOMES = [
  ...KRUX_DELIVERY_OUTCOMES,
  'not-accepted',
  'baseline-expired',
  'candidate-expired',
  'area-expired',
  'evidence-unreadable',
  /**
   * The Krux's own transition history is longer than this store will read.
   *
   * Distinct from `evidence-unreadable`, which is about the captures: here the
   * evidence is fine and the LIFECYCLE cannot be established, so nothing may be
   * concluded about a state nobody could read.
   */
  'history-unreadable',
  /**
   * The pinned baseline disagrees with the capture it names.
   *
   * The commit or run recorded at acceptance is not the one that capture
   * actually carries, so the record is internally inconsistent. Trusting either
   * half would be guessing which one is wrong.
   */
  'pin-mismatch',
  /**
   * The candidate capture is not later than the pinned baseline.
   *
   * A comparison run backwards is not a comparison. The finding could be absent
   * from an EARLIER capture simply because it had not appeared yet, and reading
   * that as a disappearance would close work before it was ever done.
   */
  'candidate-not-later',
] as const;
export const FindingDeliveryOutcomeSchema = z.enum(FINDING_DELIVERY_OUTCOMES);
export type FindingDeliveryOutcome = z.infer<
  typeof FindingDeliveryOutcomeSchema
>;

/**
 * How much of a grouped Krux the comparison resolved.
 *
 * `partial` exists because a group is not an average. One member delivered out
 * of four is real progress and is NOT delivery, and reporting it as either
 * loses the thing the reader needs.
 */
export const KRUX_GROUP_RESOLUTIONS = [
  'resolved',
  'partial',
  'unresolved',
  'not-comparable',
] as const;
export const KruxGroupResolutionSchema = z.enum(KRUX_GROUP_RESOLUTIONS);
export type KruxGroupResolution = z.infer<typeof KruxGroupResolutionSchema>;
